import React from 'react';
import {
  MdApi,
  MdStorage,
  MdPlaylistAddCheck,
  MdViewList,
  MdAssessment,
  MdCode,
  MdTrendingUp,
  MdLayers
} from 'react-icons/md';

const WorkflowStep = ({ icon: Icon, title, desc, color }) => (
  <div className="workflow-card-mini" style={{ borderTop: `4px solid ${color}` }}>
    <div className="workflow-icon-circle" style={{ backgroundColor: color }}>
      <Icon size={20} color="#fff" />
    </div>
    <h4>{title}</h4>
    <p>{desc}</p>
  </div>
);

const ChartBar = ({ height, label, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
    <div style={{
      width: '30px',
      height: `${height}px`,
      backgroundColor: color,
      borderRadius: '4px 4px 0 0',
      transition: 'height 1s ease-out'
    }} />
    <span style={{ fontSize: '10px', marginTop: '4px', color: 'var(--text-muted)' }}>{label}</span>
  </div>
);

function About() {
  return (
    <div className="about-page" style={{ paddingBottom: '50px' }}>
      {/* Hero Section */}
      <div className="about-hero" style={{
        padding: '40px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, var(--bg-card) 0%, rgba(245, 158, 11, 0.04) 100%)',
        border: '1px solid var(--border)',
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '32px', marginBottom: '10px', background: 'linear-gradient(90deg, var(--primary), #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Next-Gen API Automation
        </h1>
        <p style={{ fontSize: '18px', color: 'var(--text-main)', maxWidth: '800px', margin: '0 auto' }}>
          Simplicity of CSV meets the power of Dynamic AI. Scale your testing from 1 to 1,000,000 requests without writing a single line of boilerplate code.
        </p>
      </div>

      {/* Visual Journey Diagram */}
      <h2 style={{ textAlign: 'center', marginBottom: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
        <MdLayers color="var(--primary)" /> The Automation Journey
      </h2>
      <div className="workflow-container" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '20px',
        marginBottom: '60px',
        position: 'relative',
        flexWrap: 'wrap'
      }}>
        {/* Connector Line (Desktop Only) */}
        <div className="hide-mobile" style={{
          position: 'absolute',
          top: '40px',
          left: '50px',
          right: '50px',
          height: '2px',
          background: 'dashed 2px var(--border)',
          zIndex: 0,
          borderBottom: '2px dashed var(--border)'
        }} />

        <WorkflowStep
          icon={MdApi}
          title="1. Registry"
          desc="Define your API endpoints and auth methods once."
          color="var(--primary)"
        />
        <WorkflowStep
          icon={MdStorage}
          title="2. Data Set"
          desc="Upload CSVs. Each row becomes a test scenario."
          color="#10b981"
        />
        <WorkflowStep
          icon={MdPlaylistAddCheck}
          title="3. Logic"
          desc="Map CSV columns to API parameters with {{variables}}."
          color="#f59e0b"
        />
        <WorkflowStep
          icon={MdViewList}
          title="4. Engine"
          desc="Test Suite executes scenarios in parallel at scale."
          color="#6366f1"
        />
        <WorkflowStep
          icon={MdAssessment}
          title="5. Insight"
          desc="Get deep visual reports and pass/fail metrics."
          color="#ec4899"
        />
      </div>

      <div className="grid">
        {/* Efficiency Chart Card */}
        <div className="card" style={{ flex: 1.2 }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <MdTrendingUp color="var(--success)" /> Testing Efficiency
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
            Traditional manual testing time grows linearly. Spider Web Automation keeps execution time nearly constant, regardless of data volume.
          </p>
          <div style={{ display: 'flex', alignItems: 'flex-end', height: '150px', gap: '15px', paddingBottom: '10px', borderBottom: '2px solid var(--border)' }}>
            <ChartBar height={30} label="10 Rows" color="rgba(59, 130, 246, 0.4)" />
            <ChartBar height={60} label="100 Rows" color="rgba(59, 130, 246, 0.6)" />
            <ChartBar height={90} label="1k Rows" color="rgba(59, 130, 246, 0.8)" />
            <ChartBar height={120} label="10k Rows" color="var(--primary)" />
            <ChartBar height={145} label="Legacy Manual" color="var(--error)" />
          </div>
          <div style={{ marginTop: '15px', fontSize: '12px', textAlign: 'center', color: 'var(--text-muted)' }}>
            Execution Time per 1,000 Requests
          </div>
        </div>

        {/* Feature Highlights */}
        <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div className="card" style={{ padding: '20px', background: 'rgba(99, 102, 241, 0.05)', borderLeft: '4px solid #6366f1' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 10px 0' }}>
              <MdCode /> Dynamic AI Functions
            </h4>
            <p style={{ fontSize: '13px', margin: 0 }}>
              Use OpenAI to generate complex Python logic like HMAC signatures or data masks on the fly.
            </p>
          </div>
          <div className="card" style={{ padding: '20px', background: 'rgba(245, 158, 11, 0.05)', borderLeft: '4px solid #f59e0b' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 10px 0' }}>
              <MdStorage /> Variable Injection
            </h4>
            <p style={{ fontSize: '13px', margin: 0 }}>
              Seamlessly bridge CSV data to API bodies using the universal <code>{"{{"}variable{"}}"}</code> syntax.
            </p>
          </div>
        </div>
      </div>

      {/* Pro Tips Section */}
      <h3 style={{ marginTop: '40px', marginBottom: '20px' }}>Deep Dive into Automation</h3>
      <div className="grid">
        <div className="card">
          <h4>Custom Extensions</h4>
          <p style={{ fontSize: '14px' }}>
            Don't reinvent the wheel. Use the **Default Extensions** library to quickly add UUID generators, random name makers, and date formatters to your arsenal.
          </p>
        </div>
        <div className="card">
          <h4>Sequential Orchestration</h4>
          <p style={{ fontSize: '14px' }}>
            Test Suites don't just run APIs—they run workflows. Chain multiple steps together to test complex user journeys (e.g., Login → Create Order → Verify).
          </p>
        </div>
      </div>

      <div className="card" style={{ marginTop: '40px', background: 'var(--bg-input)', border: '1px dashed var(--primary)' }}>
        <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MdLayers color="var(--primary)" /> Pro Tip: Dynamic Data Chaining
        </h3>
        <p style={{ fontSize: '14px', marginBottom: '15px' }}>
          Want to pass a value from one step to another? Use <strong>Response Capture Rules</strong>:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '13px' }}>
          <div style={{ background: 'var(--bg-card)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <strong style={{ display: 'block', marginBottom: '8px', color: 'var(--primary)' }}>Step 2 (The Source)</strong>
            1. Go to Step Config<br />
            2. Add Capture Rule:<br />
            &nbsp;&nbsp;• <strong>Store As:</strong> <code>myVariable</code><br />
            &nbsp;&nbsp;• <strong>Path:</strong> <code>$.data.id</code>
          </div>
          <div style={{ background: 'var(--bg-card)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <strong style={{ display: 'block', marginBottom: '8px', color: 'var(--success)' }}>Step 3 (The Target)</strong>
            Use the variable in any field:<br />
            <code>/api/resource/<strong>{"{{"}myVariable{"}}"}</strong></code><br />
            or in JSON body:<br />
            <code>{"{"} "id": "<strong>{"{{"}myVariable{"}}"}</strong>" {"}"}</code>
          </div>
        </div>
      </div>

      <style>{`
        .workflow-card-mini {
          flex: 1;
          min-width: 150px;
          background: var(--bg-card);
          padding: 20px 15px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          position: relative;
          z-index: 1;
          border: 1px solid var(--border);
        }
        .workflow-icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
        }
        .workflow-card-mini h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
        }
        .workflow-card-mini p {
          margin: 0;
          font-size: 11px;
          color: var(--text-muted);
          line-height: 1.4;
        }
        @media (max-width: 1000px) {
          .hide-mobile { display: none; }
          .workflow-container { flex-direction: column; }
          .workflow-card-mini { width: 100%; text-align: left; display: flex; align-items: center; gap: 15px; }
          .workflow-icon-circle { margin: 0; }
        }
      `}</style>
    </div>
  );
}

export default About;
